import React, {createContext, useContext, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import io from 'socket.io-client'

function useForceUpdate() {
  // eslint-disable-next-line no-unused-vars
  const [value, setValue] = useState(0)
  return () => setValue((value) => value + 1) // Update state to force render.
}

const SocketContext = createContext(null)

const useSocketRef = (accessToken, host, isAuthenticationRequired, isEnabled) => {
  const socketRef = useRef(null)
  const forceUpdate = useForceUpdate()

  useEffect(() => {
    const isConnected = !!socketRef.current?.connected
    if (isEnabled && (!isAuthenticationRequired || (isAuthenticationRequired && !!accessToken))) {
      console.debug(`Socket client connecting to ${host}...`)
      socketRef.current = io.connect(host, {
        transports: ['websocket'],
        query: {
          ...(!!accessToken && {
            token: `Bearer ${accessToken}`
          })
        }
      })

      socketRef.current
        .on('connect', () => {
          console.debug(`Socket client connected to ${host}`)
          forceUpdate()
        })
        .on('disconnect', () => {
          console.debug(`Socket client disconnected from ${host}`)
          forceUpdate()
        })
        .on('error', (err) => {
          console.error('Socket client error:', err.message)
        })
    }

    return () => {
      if (isConnected) {
        console.debug(`Socket client disconnecting from ${host} before render/unmount...`)
        socketRef.current.disconnect()
      }
    }
  }, [accessToken, host, isAuthenticationRequired, isEnabled])

  return socketRef
}

const SocketProvider = ({
  accessToken,
  children,
  handleSocketEvents = () => {
    // Do nothing.
  },
  host,
  isAuthenticationRequired = true,
  isEnabled = true,
  values
}) => {
  const socketRef = useSocketRef(accessToken, host, isAuthenticationRequired, isEnabled)
  const isConnected = !!socketRef.current?.connected

  useEffect(() => {
    if (isConnected && isEnabled) {
      console.debug(`Socket client initializing socket event handlers...`)
      handleSocketEvents(socketRef.current, values)
    }
  }, [isConnected, isEnabled, JSON.stringify(values)])

  return <SocketContext.Provider value={socketRef.current}>{children}</SocketContext.Provider>
}

SocketProvider.propTypes = {
  accessToken: PropTypes.string,
  children: PropTypes.any,
  handleSocketEvents: PropTypes.func,
  host: PropTypes.string.isRequired,
  isAuthenticationRequired: PropTypes.bool,
  isEnabled: PropTypes.bool,
  values: PropTypes.object
}

export {SocketProvider}

export const useSocket = () => useContext(SocketContext)
